import SubstrateSearchRequest from '../data/schema/SubstrateSearchRequest';
import buildQueryParams from '../helpers/buildQueryParams';
import { getGuid } from 'owa-guid';
import { makePostRequest } from 'owa-ows-gateway';
import { getLocalTime } from 'owa-search-service';
import { getSubstrateSearchEndpoint, ConnectedAccountInfo } from 'owa-search-service';
import createFetchOptions from 'owa-service/lib/createFetchOptions';
import { trace } from 'owa-trace';
import { getUserCulture } from 'owa-session-store';

/**
 * This function allows caller to hit 3S service directly by providing a
 * SubstrateSearchRequest object. Instead of receiving a SubstrateSearchResponse
 * object back directly, the caller will be required to check the response
 * and the response status, and act accordingly (i.e. extracting the results
 * from the response body or handling an error from the API).
 *
 * @param substrateSearchRequest The request object for 3S query request
 * @param searchSessionId The ID identifying the user's current search session (client-session-id header value)
 * @param xClientFlightsHeaderValue Flight names to pass to the 3S service to enable server-side features
 * @param connectedAccountInfo ConnectedAccountInfo object in order to stamp the anchorMailbox and authorization info
 * for searching in multi account scenarios.
 * @returns [response, headers] - "response" will be the actual respons from
 * the 3S call. "headers" will be the custom headers generated and passed with
 * the response (required for instrumentation purposes). returns ["error"] in case of web exception
 */
export default function substrateSearchService(
    substrateSearchRequest: SubstrateSearchRequest,
    searchSessionId: string,
    xClientFlightsHeaderValue?: string[],
    connectedAccountInfo?: ConnectedAccountInfo[]
) {
    const requestHeaders = createSubstrateSearchQueryHeaders(
        substrateSearchRequest,
        searchSessionId,
        xClientFlightsHeaderValue,
        connectedAccountInfo
    );

    const queryParamValues = buildQueryParams();
    const queryParamString = queryParamValues ? `?${queryParamValues}` : '';

    return makePostRequest(
        `${getSubstrateSearchEndpoint('query')}${queryParamString}`,
        substrateSearchRequest /* requestObject */,
        getGuid() /* correlationId (client-request-id) */,
        true /* returnFullResponse */,
        requestHeaders /* customHeaders */,
        undefined /* throwServiceError */,
        undefined /* sendPayloadAsBody */,
        false /* includeCredentials */
    )
        .then(async (response: any) => {
            return [response, requestHeaders];
        })
        .catch((error: any) => {
            trace.warn(error);
            return [error];
        });
}

function createSubstrateSearchQueryHeaders(
    request: SubstrateSearchRequest,
    searchSessionId: string,
    xClientFlightsHeaders: string[],
    connectedAccountInfo: ConnectedAccountInfo[]
) {
    // Create collection of headers, and initialize it with headers from fetchOptions.
    const fetchOptions = createFetchOptions();
    const headers: any = {};

    // Prior to Firefox v50, Firefox does not support headers.forEach.
    const fetchHeaders = new Headers(fetchOptions.headers);
    if (typeof fetchHeaders.forEach === 'function') {
        fetchHeaders.forEach((val, key) => {
            headers[key] = val;
        });
    } else {
        const iter = fetchHeaders.entries();
        var item = iter.next();
        while (!item.done) {
            const [key, value] = item.value;
            headers[key] = value;
            item = iter.next();
        }
    }

    // Add custom headers.
    headers['client-session-id'] = searchSessionId;
    headers['x-client-localtime'] = getLocalTime();
    headers['X-Search-Griffin-Version'] = 'GWSv2';
    headers['Accept-Language'] = getUserCulture();

    if (connectedAccountInfo && connectedAccountInfo.length > 0) {
        headers['x-anchormailbox'] = connectedAccountInfo[0].primarySmtpAddress;
        headers['authorization'] = connectedAccountInfo[0].tokenString;
    }

    /**
     * These are flights required by the service to enable features on their end.
     * If there are no flights passed in, don't set the header. Edge v38 doesn't
     * implement the fetch API correctly and requests fail if any header is set
     * to any empty string.
     */
    const xClientFlightsHeaderValue = xClientFlightsHeaders ? xClientFlightsHeaders.join(',') : '';
    if (xClientFlightsHeaderValue) {
        headers['X-Client-Flights'] = xClientFlightsHeaderValue;
    }

    // an instrumentation tag that allows us to differentiate the various query scenarios
    const scenarioTagKey = 'scenariotag';
    headers[scenarioTagKey] = buildScenarioTag(request);

    return headers;
}

function buildScenarioTag(request: SubstrateSearchRequest) {
    const hasMultipleEntityRequests = request.EntityRequests.length > 1;
    const entityRequest = request.EntityRequests[0];
    const scenarioTags = [];
    if (hasMultipleEntityRequests) {
        scenarioTags.push('ErrMER');
    }

    if (entityRequest.From == 0) {
        scenarioTags.push('1stPg');
    }

    const shortItemType = entityTypeShortName[entityRequest.EntityType];
    scenarioTags.push(shortItemType || 'Unknown');

    return scenarioTags.join('_');
}

const entityTypeShortName: any = {
    Event: 'ev',
    Conversation: 'cv',
    Email: 'em',
    Message: 'mg',
    MailItems: 'mi',
    CalendarItems: 'ci',
    MailConversations: 'mc',
    MessageItems: 'gi',
};
